<template>
  <dashboard-template>
    <template slot="dashboard-header">
      <h6>{{ $t('global.settings') }}</h6>
    </template>
    <template slot="dashboard-body">
      <ValidationObserver ref="observer" v-slot="{ invalid }">
        <contact-modal></contact-modal>
        <div class="dashboard-content">
          <div class="d-flex justify-content-between">
            <h6>{{ $t('settings.customer_data') }}</h6>
            <button @click="saveProfileData" :disabled="invalid || !isPhoneNumberValid" type="button" class="btn btn-small btn-orange">
              <save-icon></save-icon>
              <span>{{ $t('global.save') }}</span>
            </button>
          </div>
          <div class="d-flex">
            <div class="mr-4 customer-name">
              <p>{{ $t('auth.name') }}</p>
              <validation-provider mode="aggressive" rules="required" v-slot="{ errors }">
                <input v-model="name" class="form-control" name="profileName" type="text" :placeholder="$t('auth.name')">
                <span class="validation-error-required" v-if="errors[0]">{{ $t('validations.required') }}</span>
              </validation-provider>
            </div>
            <div class="mr-4 customer-surname">
              <p>{{ $t('auth.surname') }}</p>
              <validation-provider mode="aggressive" rules="required" v-slot="{ errors }">
                <input v-model="surname" class="form-control" name="profileSurname" type="text" :placeholder="$t('auth.surname')">
                <span class="validation-error-required" v-if="errors[0]">{{ $t('validations.required') }}</span>
              </validation-provider>
            </div>
            <div class="w-25">
              <p>{{ $t('settings.phone_number') }}</p>
              <VuePhoneNumberInput
                @update="validatePhoneNumber"
                v-model="phone"
                :border-radius="5"
                :color="'#2e343d'"
                :no-use-browser-locale="true"
                :translations="phoneNumberTranslations">
              </VuePhoneNumberInput>
            </div>
          </div>
        </div>
      </ValidationObserver>
      <ValidationObserver ref="observer" v-slot="{ invalid }">
        <div class="dashboard-content">
          <div class="d-flex justify-content-between">
            <h6>{{ $t('settings.company_data') }}</h6>
            <button @click="saveProfileData" :disabled="invalid" type="button" class="btn btn-small btn-orange">
              <save-icon></save-icon>
              <span>{{ $t('global.save') }}</span>
            </button>
          </div>
          <div>
            <p>{{ $t('settings.company_name') }}</p>
            <input v-model="companyName" class="form-control" name="companyName" type="text">
          </div>
        </div>
      </ValidationObserver>
      <div class="dashboard-content">
        <div class="d-flex justify-content-between">
          <h6>{{ $t('settings.language') }}</h6>
          <button @click="saveLanguage(selectedLanguage.name)" type="button" class="btn btn-small btn-orange">
            <save-icon></save-icon>
            <span>{{ $t('global.save') }}</span>
          </button>
        </div>
        <div>
          <p>{{ $t('settings.choose_language') }}</p>
          <multiselect
            @select="onLanguageSelect"
            class="language-change-multiselect"
            label="label"
            track-by="id"
            v-model="selectedLanguage"
            :allow-empty="false"
            :deselect-label="''"
            :options="languages"
            :searchable="false"
            :select-label="''"
            :selected-label="''">
            <template slot="option" slot-scope="props">
              <span class="option__title w-100 d-flex justify-content-between align-items-center">
                <span>{{ props.option.label }}</span>
                <img v-bind:src="'/app/images/languages/'+props.option.name+'.png'" />
              </span>
            </template>
          </multiselect>
        </div>
      </div>
      <div class="dashboard-content">
        <div class="d-flex justify-content-between mb-4">
          <h6>Plan</h6>
          <button @click="$bvModal.show('contactModal')" type="button" class="btn btn-large btn-orange">
            <arrow-up-icon></arrow-up-icon>
            <span>{{ $t('settings.user_plan_change') }}</span>
          </button>
        </div>
        <div v-if="planLevel !== null" class="w-50 user-plan-container">
          <div :class="['user-plan-header', this.planLevel]">
            <h2 class="user-plan-heading">{{ $t('settings.user_plan_package') }} {{ this.planLevel }}</h2>
          </div>
          <div class="user-plan-body">
            <div v-if="this.planLevel === 'basic'">
              <ul>
                <li>{{ $t('settings.user_plan_basic_info1') }}</li>
                <li>{{ $t('settings.user_plan_basic_info2') }}</li>
                <li>{{ $t('settings.user_plan_basic_info3') }}</li>
              </ul>
              <div class="px-4">
                <h3 :class="['user-plan-price', this.planLevel]">{{ $t('settings.user_plan_basic_price1') }}</h3>
                <p>{{ $t('settings.user_plan_basic_price2') }}</p>
                <p>{{ $t('settings.user_plan_basic_price3') }}</p>
              </div>
            </div>
            <div v-else-if="this.planLevel === 'pro'">
              <ul>
                <li>{{ $t('settings.user_plan_pro_info1') }}</li>
                <li>{{ $t('settings.user_plan_pro_info2') }}</li>
                <li>{{ $t('settings.user_plan_pro_info3') }}</li>
              </ul>
              <div class="px-4">
                <h3 :class="['user-plan-price', this.planLevel]">{{ $t('settings.user_plan_pro_price1') }}</h3>
                <p>{{ $t('settings.user_plan_pro_price2') }}</p>
                <p>{{ $t('settings.user_plan_pro_price3') }}</p>
              </div>
            </div>
            <div v-else-if="this.planLevel === 'enterprise'">
              <ul>
                <li>{{ $t('settings.user_plan_enterprise_info1') }}</li>
              </ul>
              <div class="px-4">
                <h3 :class="['user-plan-price', this.planLevel]">{{ $t('settings.user_plan_enterprise_price1') }}</h3>
                <p>{{ $t('settings.user_plan_enterprise_price2') }}</p>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="w-50 user-plan-container">
          <div class="alert alert-danger mb-4 mt-3" role="alert">
            <p class="mb-0"><strong>{{ $t('settings.user_plan_error1') }}</strong></p>
            <p class="mb-0">{{ $t('settings.user_plan_error2') }}</p>
            <p class="mb-0">{{ $t('settings.user_plan_error3') }}</p>
          </div>
        </div>
      </div>
    </template>
  </dashboard-template>
</template>

<script>
import axios from 'axios'
import DashboardTemplate from '@/views/Dashboard'
import Multiselect from 'vue-multiselect'
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate'
import { required } from 'vee-validate/dist/rules'
import VuePhoneNumberInput from 'vue-phone-number-input'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'
import { ArrowUpIcon, SaveIcon } from 'vue-feather-icons'
import ContactModal from './ContactModal'

extend('required', {
  ...required
})

export default {
  name: 'Settings',

  data () {
    return {
      companyName: null,
      formattedPhoneNumber: null,
      isPhoneNumberValid: false,
      languages: [],
      name: null,
      phone: null,
      phoneNumberTranslations: {},
      planLevel: null,
      selectedLanguage: {},
      surname: null
    }
  },

  created () {
    if (this.$cookies.get('language_version') !== null) {
      this.initializeLanguages()
      this.initializePhoneNumberTranslations()
    }
    this.getProfileData()
  },

  methods: {
    getProfileData () {
      axios.get(process.env.VUE_APP_API_ENDPOINT + '/auth/profile')
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            this.name = response.data.userData.name
            this.surname = response.data.userData.surname
            this.planLevel = response.data.userData.planLevel
            this.phone = response.data.userData.phoneNumber
            this.companyName = response.data.userData.company
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$router.push({ name: 'auth.error', params: { errorCode: error.response.data.statusCode, errorMessage: error.response.data.message } })
          }
        })
    },

    onLanguageSelect (language) {
      this.selectedLanguage = language
    },

    saveProfileData () {
      axios.post(process.env.VUE_APP_API_ENDPOINT + '/auth/user/data', { name: this.name, surname: this.surname, phoneNumber: this.formattedPhoneNumber, company: this.companyName, dealId: null, planLevel: this.planLevel })
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            this.$bvToast.toast(this.$t('settings.save_success_description'), {
              autoHideDelay: 3000,
              solid: true,
              title: this.$t('settings.save_success_title'),
              variant: 'success'
            })
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$bvToast.toast(this.$t('settings.save_error_description'), {
              autoHideDelay: 3000,
              solid: true,
              title: this.$t('settings.save_error_title'),
              variant: 'danger'
            })
          }
        })
    },

    saveLanguage (languageCode) {
      axios.post(process.env.VUE_APP_API_ENDPOINT + '/auth/user/language', { language: languageCode })
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            this.$i18n.locale = languageCode
            this.$cookies.set('language_version', languageCode)
            this.$moment.locale(languageCode)
            this.$parent.generateSidebarMenu()
            this.$bvToast.toast(this.$t('settings.change_language_success_description'), {
              autoHideDelay: 3000,
              solid: true,
              title: this.$t('settings.change_language_success_title'),
              variant: 'success'
            })
            this.initializeLanguages()
            this.initializePhoneNumberTranslations()
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$bvToast.toast(this.$t('settings.change_language_error_description'), {
              autoHideDelay: 3000,
              solid: true,
              title: this.$t('settings.change_language_error_title'),
              variant: 'danger'
            })
          }
        })
    },

    validatePhoneNumber (value) {
      this.formattedPhoneNumber = value.formattedNumber
      this.isPhoneNumberValid = value.isValid
    },

    initializeLanguages () {
      this.languages = [
        { id: 1, label: this.$t('settings.polish'), name: 'pl' },
        { id: 2, label: this.$t('settings.english'), name: 'en' }
      ]
      this.selectedLanguage = this.languages.find(language => this.$cookies.get('language_version') === language.name)
    },

    initializePhoneNumberTranslations () {
      this.phoneNumberTranslations = {
        countrySelectorLabel: this.$t('settings.phone_number_country_selector_label'),
        countrySelectorError: this.$t('settings.phone_number_country_selector_error'),
        phoneNumberLabel: this.$t('settings.phone_number_label'),
        example: this.$t('settings.phone_number_example')
      }
    }
  },

  components: { ArrowUpIcon, ContactModal, DashboardTemplate, Multiselect, SaveIcon, ValidationObserver, ValidationProvider, VuePhoneNumberInput }
}
</script>
